import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid2, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { updatePartCampaignOffset } from "../../repositories/PartsReportRepository";

export const UpdatePartCampaignModal = ({
	open,
	onHide,
	setNotificationMessage,
	partId,
	partCampaignId,
	info = { partName: "", partType: "", campaignWeight: 0, offsetWeight: 0 },
}: {
	open: boolean;
	onHide: () => void;
	setNotificationMessage: (message: string) => void;
	partId: number;
	partCampaignId: number;
	info: {
		partName?: string | null;
		partType?: string | null;
		campaignWeight?: number;
		offsetWeight?: number;
	};
}) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");

	const { control, reset, getValues, setValue } = useForm({
		defaultValues: { campaignWeight: 0 },
		mode: "onChange",
	});

	const queryClient = useQueryClient();
	const updatePartCampaign = useMutation(updatePartCampaignOffset, {
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["parts", partId],
			});
			await queryClient.invalidateQueries({
				queryKey: ["partCampaignData", partId],
			});
			setNotificationMessage("Part campaign updated successfully");
			onHide();
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const handleSubmit = () => {
		setIsSubmitLoading(true);
		const offsetWeight =
			getValues("campaignWeight") - (info?.campaignWeight ?? 0);
		updatePartCampaign.mutate({
			partCampaignId: partCampaignId ?? null,
			offsetWeight: offsetWeight ?? 0,
		});
	};

	useEffect(() => {
		reset();
		if (open) {
			setValue(
				"campaignWeight",
				(info.campaignWeight ?? 0) + (info.offsetWeight ?? 0),
			);
		}
	}, [open, info.campaignWeight, reset, setValue]);

	return (
		<>
			<MesfModal
				title="UPDATE CAMPAIGN WEIGHT"
				open={open}
				id="update-campaign-modal"
				maxWidth="sm"
				handleClose={onHide}
			>
				<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="campaignWeight"
								control={control}
								rules={{
									required: "Campaign weight is required",
									min: {
										value: 0,
										message: "Campaign weight must be positive",
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<TextField
										value={field.value || ""}
										onChange={(e) => field.onChange(Number(e.target.value))}
										label="Campaign Weight"
										variant="outlined"
										size="small"
										type="number"
										fullWidth
										error={!!error}
										helperText={error?.message}
										inputProps={{ step: "0.01" }}
									/>
								)}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={onHide}
								disabled={isSubmitLoading}
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
							<Button
								fullWidth
								onClick={handleSubmit}
								startIcon={
									isSubmitLoading ? (
										<CircularProgress size="1rem" color="inherit" />
									) : null
								}
								variant="contained"
								color="primary"
								disabled={isSubmitLoading}
							>
								Update
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
